import { BackArrowIcon, CopyIcon, LeftArrowIcon, RightArrowIcon, SearchIcon, SortDownIcon } from "@/assets/icons/admin";
import { useEditBulkReferrals, useGetSpecialReferrals } from "@/services/admin.service";
import { SpecialReferralDetails } from "@/types";
import { Button, Checkbox, getKeyValue, Input, Table, TableBody, TableCell, TableColumn, TableHeader, TableRow, Textarea } from "@nextui-org/react";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";

interface AddRefListProps {
    onCancel: () => void;
}

const AddRefList: React.FC<AddRefListProps> = ({ onCancel }) => {
    const [searchKeyword, setSearchKeyword] = useState<string>("");
    const [sortColumn, setSortColumn] = useState<string | null>(null);
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

    const handleSearchKeywordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchKeyword(e.target.value);
    };

    const columns = [
        {
            key: "box",
            label: "Box",
        }, {
            key: "no",
            label: "No",
        },
        {
            key: "createdAt",
            label: "Received Date",
        },
        {
            key: "userName",
            label: "Username",
        },
        {
            key: "fullName",
            label: "Fullname",
        },
        {
            key: "email",
            label: "Email",
        },
        {
            key: "specialRate",
            label: "Special Rates (%)",
        },
        {
            key: "tierLevel",
            label: "Tier",
        }, {
            key: "refLink",
            label: "Ref.Links",
        },
    ]

    const [tableRows, setTableRows] = useState<SpecialReferralDetails[]>([]);
    const { data: getAllHistory, refetch } = useGetSpecialReferrals({ page: 1, pageSize: 10 });
    const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
    const [specialRate, setSpecialRate] = useState<number>(0);
    const [reason, setReason] = useState<string>('');

    useEffect(() => {
        if (getAllHistory?.data?.data.length > 0) {
            setTableRows(getAllHistory?.data?.data);
        }
    }, [getAllHistory?.data?.data]);

    const handleCopyText = (text: string) => {
        navigator.clipboard
            .writeText(text)
            .then(() => {
                console.log("Text copied to clipboard:", text);
            })
            .catch((error) => {
                console.error("Error copying text to clipboard:", error);
            });
    };

    const [currentPage, setCurrentPage] = useState(1);
    const rowsPerPageOptions = ["All", "3", "5", "10"];
    const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);

    const handleChangeRowsPerPage = (e: any) => {
        setRowsPerPage(e.target.value);
        setCurrentPage(1);
    };

    const handleClickNextPage = () => {
        setCurrentPage((prevPage) => prevPage + 1);
    };

    const handleClickPrevPage = () => {
        setCurrentPage((prevPage) => Math.max(prevPage - 1, 1));
    };

    const applyFilters = () => {
        let filteredRows = tableRows;

        // Apply search filter
        if (searchKeyword) {
            const lowerCaseSearchKeyword = searchKeyword.toLowerCase();
            filteredRows = filteredRows.filter(row => {
                const userNameMatch = row.userName?.toLowerCase().includes(lowerCaseSearchKeyword);
                const emailMatch = row.email?.toLowerCase().includes(lowerCaseSearchKeyword);
                return userNameMatch || emailMatch; // Adjust as needed to match more fields
            });
        }

        // Apply other filters here (date, status, tier, etc.)

        // Apply sorting
        if (sortColumn) {
            filteredRows.sort((a, b) => {
                const aValue = a[sortColumn as keyof typeof a];
                const bValue = b[sortColumn as keyof typeof b];
                if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
                if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
                return 0;
            });
        }

        return filteredRows;
    };

    let paginatedRows = applyFilters();
    if (rowsPerPage !== "All") {
        paginatedRows = tableRows.slice(
            (currentPage - 1) * parseInt(rowsPerPage),
            currentPage * parseInt(rowsPerPage),
        );
    }

    const totalRows = tableRows.length;
    const startRow = (currentPage - 1) * paginatedRows.length + 1;
    const endRow = Math.min(currentPage * paginatedRows.length, totalRows);

    const rowsRange = `${startRow}-${endRow} out of ${totalRows}`;

    const handleSort = (columnKey: string) => {
        if (sortColumn === columnKey) {
            // Toggle sort direction if the same column is clicked
            setSortDirection(prevDirection => (prevDirection === 'asc' ? 'desc' : 'asc'));
        } else {
            // Set new column and default to ascending
            setSortColumn(columnKey);
            setSortDirection('asc');
        }
    };

    const handleCheckboxChange = (userId: string) => {
        setSelectedUserIds(prevSelected => {
            const newSelected = new Set(prevSelected);
            if (newSelected.has(userId)) {
                newSelected.delete(userId);
            } else {
                newSelected.add(userId);
            }
            return newSelected;
        });
    };

    const handleSpecialRateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        // Convert input value to a number
        const value = parseFloat(e.target.value);
        setSpecialRate(isNaN(value) ? 0 : value); // Handle case where conversion fails
    };
    const editReferrals = useEditBulkReferrals()
    const handleSave = () => {
        if (selectedUserIds.size === 0) {
            toast.error("Select records to add referrals")
        }

        const idsArray = Array.from(selectedUserIds);

        editReferrals.mutate(
            {
                reason,
                rate: specialRate,
                ids: idsArray
            },
            {
                onSuccess: () => {
                    console.log("Edited referrals");
                    refetch();
                    toast.success("Edited referrals");
                },
                onError: (error: any) => {
                    console.log("Self-exclusion request failed!");
                    console.log(error);

                    toast.error("Adding referrals failed")
                },
            },
        );
    }

    return (
        <>
            <div>
                <div className="font-ubuntu mb-4 w-full text-center text-2xl font-semibold text-white sm:float-left">
                    <span>
                        <Button
                            isIconOnly
                            onClick={onCancel}
                            className="transparent-bg float-left ml-[-11px] h-auto w-auto"
                        >
                            <BackArrowIcon />
                        </Button>
                        <p className="float-left text-xl">
                            Add Special Referral
                        </p>
                    </span>
                </div>
                <div className="flex w-full flex-col gap-4">
                    <div className="mb-6 flex w-auto flex-wrap items-start gap-4 mt-2 sm:mt-2 md:mb-0 md:flex-nowrap">
                        <Input
                            className="input-bg sm:w-2/3 w-[55%]"
                            isClearable
                            classNames={{
                                input: [
                                    "mr-0",
                                    "text-white/90",
                                    "placeholder:text-default-700/50 dark:placeholder:text-white/60",
                                ],
                                inputWrapper: [
                                    "shadow-xl",
                                    "!cursor-text",
                                    "bg-white-5",
                                    "text-[11px]"
                                ],
                            }}
                            placeholder="Search Username or email"
                            endContent={<SearchIcon />}
                            onChange={handleSearchKeywordChange}
                        ></Input>
                        <Button className="admin-common-button-gradient font-ubuntu w-[40%] sm:w-[155px] rounded-md text-base font-bold">
                            Apply
                        </Button>
                    </div>
                </div>
                <div className="w-full sm:mt-5">
                    <div className="w-full overflow-x-auto">
                        <Table removeWrapper className="table-striped table table-striped-even">
                            <TableHeader className="rounded-none" columns={columns}>
                                {(column) => (
                                    <TableColumn key={column.key}>
                                        <div className="flex white-op">
                                            {column.key === "box" ? (
                                                <input type="checkbox" className="ref-checkbox" />
                                            ) : (
                                                <>
                                                    {column.label}
                                                    <Button onClick={() => handleSort(column.key)} isIconOnly className="transparent-bg h-auto w-auto">
                                                        <SortDownIcon />
                                                    </Button>
                                                </>
                                            )}
                                        </div>
                                    </TableColumn>
                                )}
                            </TableHeader>
                            <TableBody items={paginatedRows}>
                                {(item) => (
                                    <TableRow key={item.id}>
                                        {(columnKey) => (
                                            <TableCell>
                                                {columnKey === "no" ? (
                                                    <div className="font-openSans text-sm font-bold">
                                                        {" "}
                                                        {tableRows.findIndex(
                                                            (row) => (row as any)?.id === (item as any)?.id,
                                                        ) +
                                                            1 +
                                                            "."}
                                                    </div>
                                                ) : columnKey === "box" ? (
                                                    <input
                                                        type="checkbox"
                                                        className="ref-checkbox"
                                                        onChange={() => handleCheckboxChange(item.id)}
                                                    />
                                                ) : columnKey === "userName" ? (
                                                    <div className="font-bold tb-data-blue">
                                                        {" "}
                                                        {getKeyValue(item, columnKey)}
                                                    </div>
                                                ) : columnKey === "specialRate" ? (
                                                    <div className="text-right mr-10 font-bold tb-data-blue">
                                                        {" "}
                                                        {getKeyValue(item, columnKey) + "%"}
                                                    </div>
                                                ) : columnKey === "tierLevel" ? (
                                                    <div className="font-bold">
                                                        {" "}
                                                        {getKeyValue(item, columnKey)}
                                                    </div>
                                                ) : columnKey === "refLink" ? (
                                                    <div className="items-center font-normal">
                                                        <span className="flex items-center">
                                                            <p>{getKeyValue(item, columnKey)}</p>
                                                            <Button
                                                                isIconOnly
                                                                onClick={() => handleCopyText(getKeyValue(item, columnKey))}
                                                                className="transparent-bg h-6 w-6"
                                                            >
                                                                <CopyIcon />
                                                            </Button>
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <div className="font-normal">
                                                        {" "}
                                                        {getKeyValue(item, columnKey)}
                                                    </div>
                                                )}
                                            </TableCell>
                                        )}
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>
                    <div className="mt-1 flex	items-center table-striped-even text-sm sm:justify-end sm:text-base">
                        <div className="ml-[15px] justify-start sm:ml-0 sm:ml-4 sm:mr-8">
                            <label
                                htmlFor="rowsPerPage"
                                className="font-sans opacity-30 sm:mr-2"
                            >
                                Rows per page:
                            </label>
                            <select
                                id="rowsPerPage"
                                className="select-menu font-open-sans rounded border-none bg-transparent px-2 py-1 font-normal text-gray-500"
                                value={rowsPerPage}
                                onChange={handleChangeRowsPerPage}
                            >
                                {rowsPerPageOptions.map((option) => (
                                    <option
                                        key={option}
                                        value={option}
                                        className="font-open-sans text-gray-600 sm:font-normal"
                                    >
                                        {option}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="flex">
                            <div className="ml-[8px] mt-2 opacity-30 sm:ml-[45px] sm:mr-8">
                                <p>{rowsRange}</p>
                            </div>
                            <button
                                onClick={handleClickPrevPage}
                                className="ml-[18px] mr-2 text-xs sm:ml-0 sm:mr-3"
                            >
                                <LeftArrowIcon />
                            </button>
                            <button onClick={handleClickNextPage} className="text-xs">
                                <RightArrowIcon />
                            </button>
                        </div>
                    </div>
                </div>
                {/* Input form */}
                <div className="flex w-full flex-col pt-10">
                    <div className="flex flex-col">
                        <div className="mt-1 basis-full sm:mt-0 sm:basis-full">
                            <label
                                htmlFor="firstName"
                                className="field-label mb-2 block text-sm font-normal text-white"
                            >
                                Special Rates (%)
                            </label>
                            <Input
                                type="number"
                                name="title"
                                onChange={handleSpecialRateChange}
                                className={`w-full rounded-md border border-solid sm:w-full input-bg !cursor-text`}
                                classNames={{
                                    inputWrapper: [
                                        "!cursor-text",
                                        "bg-white-5",
                                        "text-sm"
                                    ],
                                }}
                                placeholder="Type Here"
                            ></Input>
                        </div>
                        <div className="basis-full mt-4 sm:basis-full">
                            <label
                                htmlFor="firstName"
                                className="field-label mb-2 block text-sm font-normal text-white"
                            >
                                Reason
                            </label>
                            <Textarea
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                className={`input-bg !cursor-text rounded-md border border-solid`}
                                classNames={{
                                    inputWrapper: [
                                        "!cursor-text",
                                        "bg-white-5",
                                        "reason-text-field-height", "text-sm"
                                    ],
                                }}
                                variant="bordered"
                                defaultValue={``}
                                placeholder="Type Here"
                            />
                        </div>
                        <div className="mt-4 basis-1/2 ">
                            <div className="sm:float-right">
                                <Button onClick={handleSave} className="admin-common-button-gradient font-ubuntu w-full rounded-md pl-20 pr-20 text-base font-bold">
                                    Save
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default AddRefList;